import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

function ParentDashboard() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    missionCount: 0,
    pendingApprovals: 0,
    pendingRewards: 0,
    weeklyCompletionRate: 0,
    estimatedPayment: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        // 부모가 아닌 경우 홈으로 리다이렉트
        navigate('/');
        return;
      }
      setUser(parsedUser);

      // 대시보드 통계 데이터 로드
      fetchDashboardStats();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/parent/login');
    }
  }, [navigate]);

  const fetchDashboardStats = async () => {
    try {
      // 미션 개수 조회
      const missionsResponse = await api.get('/missions');

      // 승인 대기 목록 조회
      const pendingResponse = await api.get('/assignments/pending-approvals');

      // 주간 정산 미리보기 조회
      const settlementResponse = await api.get('/settlements/preview');

      // 선물 요청 대기 목록 조회
      const rewardsResponse = await api.get('/rewards/family-requests');

      setStats(prev => ({
        ...prev,
        missionCount: missionsResponse.data.missions?.length || 0,
        pendingApprovals: pendingResponse.data.assignments?.length || 0,
        pendingRewards: rewardsResponse.data.requests?.filter(r => r.status === 'pending').length || 0,
        weeklyCompletionRate: settlementResponse.data.summary?.averageCompletionRate || 0,
        estimatedPayment: settlementResponse.data.summary?.totalFamilyAmount || 0
      }));

    } catch (error) {
      console.error('Dashboard stats fetch error:', error);
      // 에러가 발생해도 기본값으로 설정
      setStats(prev => ({
        ...prev,
        missionCount: 0,
        pendingApprovals: 0,
        pendingRewards: 0,
        weeklyCompletionRate: 0,
        estimatedPayment: 0
      }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* 헤더 */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">🍃 도토리 저금통</h1>
          <div className="header-user">
            <span>안녕하세요, {user.name}님!</span>
            <button
              onClick={handleLogout}
              className="btn btn-outline"
              style={{ marginLeft: '10px', fontSize: '0.9rem' }}
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="dashboard-content">
        <div className="welcome-section">
          <h2>👨‍👩‍👧‍👦 {user.familyName} 관리</h2>
          <p>가족의 미션과 용돈을 관리해보세요</p>
        </div>

        {/* 빠른 액션 */}
        <div className="quick-actions">
          <h3>빠른 작업</h3>
          <div className="action-buttons">
            <button className="btn btn-outline" onClick={() => navigate('/parent/missions/new')}>
              📝 새 미션 추가
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/rewards')}>
              🎁 선물 요청
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/children')}>
              👶 자녀 관리
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/settings')}>
              ⚙️ 가족 설정
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/reports')}>
              📈 월간 리포트
            </button>
          </div>
        </div>

        {/* 대시보드 카드들 */}
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-icon">✅</div>
            <h3>승인 대기</h3>
            <p className="card-number">{stats.pendingApprovals}건</p>
            <p className="card-description">자녀들의 미션 체크를 확인해주세요</p>
            <Link to="/parent/approvals" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              승인하기
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">🎁</div>
            <h3>선물 요청</h3>
            <p className="card-number">{stats.pendingRewards}건</p>
            <p className="card-description">자녀들의 선물 요청을 확인해주세요</p>
            <Link to="/parent/rewards" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              선물 관리
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">🎯</div>
            <h3>미션 관리</h3>
            <p className="card-number">{stats.missionCount}개</p>
            <p className="card-description">새로운 미션을 추가하거나 수정하세요</p>
            <Link to="/parent/missions" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              미션 관리
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">💰</div>
            <h3>이번 주 정산</h3>
            <p className="card-number">{stats.estimatedPayment.toLocaleString()}원</p>
            <p className="card-description">이번 주 예상 지급액을 확인하세요</p>
            <button className="btn btn-primary btn-full" onClick={() => navigate('/parent/settlements')}>
              정산하기
            </button>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">📊</div>
            <h3>완료율</h3>
            <p className="card-number">{stats.weeklyCompletionRate}%</p>
            <p className="card-description">이번 주 가족 미션 완료율</p>
            <Link to="/parent/reports" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              리포트 보기
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }

        .dashboard-header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 15px 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dashboard-title {
          margin: 0;
          color: #8B4513;
          font-size: 1.5rem;
        }

        .header-user {
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .dashboard-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .welcome-section {
          text-align: center;
          margin-bottom: 40px;
        }

        .welcome-section h2 {
          color: #333;
          margin-bottom: 10px;
        }

        .welcome-section p {
          color: #666;
          font-size: 1.1rem;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 25px;
          margin-top: 40px;
        }

        .dashboard-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .dashboard-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .card-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .dashboard-card h3 {
          color: #333;
          margin-bottom: 10px;
          font-size: 1.2rem;
        }

        .card-number {
          font-size: 2rem;
          font-weight: bold;
          color: #8B4513;
          margin: 10px 0;
        }

        .card-description {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .btn-full {
          width: 100%;
        }

        .quick-actions {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          margin-bottom: 40px;
        }

        .quick-actions h3 {
          color: #333;
          margin-bottom: 20px;
          text-align: center;
        }

        .action-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 15px;
          }

          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default ParentDashboard;